import React from 'react';

import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListZones } from 'src/features/zone/api/useListZones';
import SettingsContext from 'src/features/administration/context/SettingsContext';
import { GatewayFormSchemaValues } from 'src/features/gateway/utils/formSchema';
import { REQUIRED_IDENTIFIER_TYPES } from 'src/features/gateway/utils/types';
import { requiredIdentifiersTranslationMap } from 'src/features/gateway/utils/constants';
import { InputWithInlineTitle } from 'src/registry/components/inputs/InputWithInlineTitle';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Textarea } from 'src/registry/ui/Textarea';
import { generalMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/utils/constants';

type GatewayFormProps = {
    form: UseFormReturn<GatewayFormSchemaValues>;
    onSubmit: (values: GatewayFormSchemaValues) => void;
};

const translationFields = [
    { key: 'name', label: generalMessages.name },
    { key: 'description', label: generalMessages.description },
] as const;

function GatewayForm({ onSubmit, form }: GatewayFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const settingsData = React.useContext(SettingsContext);
    const { data: zonesData } = useListZones({ parkingId });

    const selectedEntryZone = form.watch('entry_zone');
    const selectedExitZone = form.watch('exit_zone');

    const filteredEntryZones = React.useMemo(() => {
        if (selectedExitZone) {
            return zonesData?.results.filter((zone) => zone.id !== selectedExitZone);
        }
        return zonesData?.results;
    }, [zonesData, selectedExitZone]);

    const filteredExitZones = React.useMemo(() => {
        if (selectedEntryZone) {
            return zonesData?.results.filter((zone) => zone.id !== selectedEntryZone);
        }
        return zonesData?.results;
    }, [zonesData, selectedEntryZone]);

    const { fields } = useFieldArray({
        control: form.control,
        name: 'translations',
    });
    const { translations } = form.getValues();

    React.useEffect(() => {
        if (settingsData?.languages) {
            form.setValue(
                'translations',
                settingsData.languages.map((lang, index) => ({
                    language: lang,
                    name: translations[index]?.name || '',
                    description: translations[index]?.description || '',
                })),
            );
        }
    }, [settingsData, form]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {translationFields.map(({ key, label }) =>
                fields.map((_, index) => (
                    <FormField
                        key={`translations.${index}.${key}`}
                        control={form.control}
                        name={`translations.${index}.${key}`}
                        render={({ field }) => (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required={key === 'name'}>
                                    <FormattedMessage id={label.id} defaultMessage={label.defaultMessage} />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        {key === 'name' ? (
                                            <InputWithInlineTitle
                                                {...field}
                                                value={field.value ?? ''}
                                                inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                                                placeholder={`${formatMessage(label)}...`}
                                            />
                                        ) : (
                                            <Textarea
                                                {...field}
                                                value={field.value ?? ''}
                                                inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                                                placeholder={`${formatMessage(label)}...`}
                                            />
                                        )}
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        )}
                    />
                )),
            )}

            <SelectFormField<GatewayFormSchemaValues>
                form={form}
                required
                isBoolean
                name="allow_unknown_main_identifier"
                label={formatMessage(gatewayMessages.allowUnknownMainIdentifier)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <SelectFormField<GatewayFormSchemaValues>
                form={form}
                required
                isBoolean
                name="require_all_identifier"
                label={formatMessage(gatewayMessages.requireAllIdentifier)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />

            <FormField
                control={form.control}
                name="entry_zone"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...gatewayMessages.entryZone} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(gatewayMessages.entryZone)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {filteredEntryZones?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="exit_zone"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...gatewayMessages.exitZone} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(gatewayMessages.exitZone)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {filteredExitZones?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="required_identifier_types"
                render={({ field }) => {
                    const fieldData = ((field.value ?? []) as REQUIRED_IDENTIFIER_TYPES[])
                        .map((identifier: REQUIRED_IDENTIFIER_TYPES) => requiredIdentifiersTranslationMap[identifier])
                        .filter(Boolean)
                        .map((msg) => formatMessage(msg!))
                        .join(', ');

                    const allValues = Object.keys(requiredIdentifiersTranslationMap) as REQUIRED_IDENTIFIER_TYPES[];
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...gatewayMessages.requiredIdentifiers} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect
                                        value={(field.value ?? []) as REQUIRED_IDENTIFIER_TYPES[]}
                                        onChange={(values) => field.onChange(values)}
                                    >
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl className="-mr-4 w-full">
                                                {fieldData}
                                                {fieldData.length == 0 && (
                                                    <span className="text-gray-400">
                                                        {`${formatMessage(gatewayMessages.requiredIdentifiers)}...`}
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent allValues={allValues}>
                                            {Object.entries(requiredIdentifiersTranslationMap).map(([key, value]) => (
                                                <MultiSelectOption key={key} selected={key}>
                                                    <div className="flex flex-row justify-between">
                                                        {formatMessage(value)}
                                                    </div>
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
}

export default GatewayForm;
